import { RESERVED_KEYS } from "./constants";
import { Frame, NormalizedFrames, ItemConfig, DataItemValue } from "../types";

/**
 * Extracts the numeric value from a DataItemValue.
 * Handles both primitive values (number/string) and ItemConfig objects.
 */
export function extractValue(item: DataItemValue): number {
  if (item === null || item === undefined) return 0;
  if (typeof item === "number") return item;
  if (typeof item === "string") return Number(item) || 0;
  if (typeof item === "object" && "value" in item) return item.value;
  return 0;
}

/**
 * Extracts item-level configuration from a DataItemValue.
 * Returns undefined if the value is a primitive (no per-item config).
 */
export function extractItemConfig(item: DataItemValue): ItemConfig | undefined {
  if (item && typeof item === "object" && "value" in item) {
    return item as ItemConfig;
  }
  return undefined;
}

export function normalizeFrames(frames: Frame[]): NormalizedFrames {
  // Key order here is whatever Object.keys() yields: numeric-like keys first in
  // ascending numeric order, then remaining string keys in insertion order. This
  // is the order used by sort: 'none'. Treat it as non-deterministic — it is not
  // a documented contract and is not preserved across JSON round-trips (e.g.
  // Postgres JSONB). Use a value-based sort when order must be stable.
  const keys = Array.from(
    new Set(
      frames
        .flatMap((frame) => Object.keys(frame))
        .filter((key) => !RESERVED_KEYS.has(key)),
    ),
  );

  let globalMin = Number.POSITIVE_INFINITY;
  let globalMax = Number.NEGATIVE_INFINITY;

  frames.forEach((frame) => {
    keys.forEach((key) => {
      const value = extractValue(frame[key]);
      if (Number.isFinite(value)) {
        globalMin = Math.min(globalMin, value);
        globalMax = Math.max(globalMax, value);
      }
    });
  });

  if (!Number.isFinite(globalMin) || !Number.isFinite(globalMax)) {
    globalMin = 0;
    globalMax = 1;
  }

  const hasPositiveValues = globalMax > 0;
  const hasNegativeValues = globalMin < 0;
  // Anchor the domain at zero so bars render against a zero baseline. For
  // all-positive data zero sits at the bottom (clamp min); for all-negative
  // data zero sits at the top (clamp max). Mixed data keeps min<0, max>0.
  if (hasPositiveValues && !hasNegativeValues) {
    globalMin = 0;
  } else if (hasNegativeValues && !hasPositiveValues) {
    globalMax = 0;
  }

  if (globalMin === globalMax) {
    globalMin -= 1;
    globalMax += 1;
  }

  return { keys, globalMin, globalMax };
}
